import React from "react";
import SelectField from "components/formFields/SelectField";
import { MenuItem, Select, TextField, Typography } from "@mui/material";
import { sortSites } from "i18n/constants";
import { Field } from "formik";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  styled,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import AddCircleIcon from "@mui/icons-material/AddCircleOutline";

interface Props {
  siteTemplate: any;
  isActive: boolean;
  index: number;
  options: any[];
  createSiteTemplate: (siteName: string) => void;
  handleSimulationGroupChange: (siteId: string, siteGroupId: string) => void;
  handlePrescriptionChange: (siteId: string, prescriptionId: string) => void;
  handleDelete: any;
  siteGroupCount: number;
  updateSiteName: any;
  siteOptions: any;
  prescriptionOptions: any;
}

const StyledTableHead = styled(TableHead)`
  background-color: ${(props) => props.theme.palette.grey[100]};
`;

const StyledTableHeaderCell = styled(TableCell)`
  font-weight: bold;
  border-bottom-width: 0px;
`;

const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: separate;
`;

const StyledTableCell = styled(TableCell)`
  height: 72px !important;
  max-height: 72px !important;
  padding: 8px 16px !important;
`;

const CompactSelect = styled(Select)`
  height: 40px !important;
  min-height: 40px !important;
  & .MuiSelect-select {
    padding: 8px 12px !important;
    font-size: 14px !important;
  }
`;

const treatmentSiteGroupCols = [
  "Site",
  "Simulation Group",
  "Prescription Group",
  "Delete",
];

const TreatmentSitesTable = ({
  siteTemplate,
  isActive,
  index,
  options,
  createSiteTemplate,
  handleSimulationGroupChange,
  handlePrescriptionChange,
  handleDelete,
  siteGroupCount,
  updateSiteName,
  prescriptionOptions,
  siteOptions,
}: Props) => {
  const sitesWithEmpty = [...sortSites(siteTemplate), { site: { name: "" } }];
  return (
    <TableContainer>
      <StyledTable aria-label="customized table">
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <StyledTableHead>
          <TableRow>
            {treatmentSiteGroupCols.map((header, index) => (
              <StyledTableHeaderCell
                align="left"
                key={`site-group-header-${index}`}
              >
                {header}
              </StyledTableHeaderCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {sitesWithEmpty.map((siteTemplate: any, rowIndex: number) => {
            const name = siteTemplate?.site?.name;
            const baseKey = `${index}-${rowIndex}`;
            return (
              <TableRow key={baseKey}>
                <StyledTableCell
                  data-test-id={`name-${baseKey}`}
                  id={`name-${baseKey}`}
                  scope="row"
                >
                  <Autocomplete
                    id={`site-select-${baseKey}`}
                    options={siteOptions}
                    autoHighlight={true}
                    getOptionLabel={(option: {
                      label: string;
                      value: string;
                    }) => option.label}
                    value={{ label: name, value: name }}
                    disableClearable
                    isOptionEqualToValue={(
                      option: { label: string; value: string },
                      value: { value: string },
                    ): boolean => value.value === option.value}
                    onChange={(
                      _event,
                      value: { label: string; value: string } | null,
                    ) => {
                      if (value !== null) {
                        if (!siteTemplate?.id) {
                          createSiteTemplate(value.value);
                        } else {
                          updateSiteName({
                            variables: {
                              siteId: siteTemplate.id,
                              name: value.value,
                            },
                          });
                        }
                      }
                    }}
                    disabled={!isActive}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Add new site"
                        size="small"
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px !important",
                        minHeight: "40px !important",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 12px !important",
                        fontSize: "14px !important",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="center">
                  {sitesWithEmpty?.length > 2 &&
                    siteTemplate.site.name !== "" && (
                      <CompactSelect
                        id={`site-group-select-${baseKey}`}
                        name={name}
                        value={siteTemplate.siteGroupId}
                        disabled={!isActive}
                        sx={{
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "left",
                            gap: "8px",
                          },
                        }}
                        fullWidth
                        size="small"
                        onChange={(event) =>
                          handleSimulationGroupChange(
                            siteTemplate.id,
                            event.target.value as string,
                          )
                        }
                      >
                        {options.map((option: any) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            data-test-id={`site-group-select-${option.label}`}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                        <MenuItem
                          value={""}
                          data-test-id={"site-group-select-new-sim"}
                        >
                          <AddCircleIcon color="primary" />
                          <Typography sx={{ marginLeft: "8px" }}>
                            New Sim
                          </Typography>
                        </MenuItem>
                      </CompactSelect>
                    )}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {prescriptionOptions.length > 1 &&
                    siteTemplate.site.name !== "" && (
                      <CompactSelect
                        id={`prescription-select-${baseKey}`}
                        name={name}
                        value={siteTemplate.prescriptionId}
                        disabled={!isActive}
                        fullWidth
                        size="small"
                        onChange={(event) =>
                          handlePrescriptionChange(
                            siteTemplate.id,
                            event.target.value as string,
                          )
                        }
                        sx={{
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "left",
                            gap: "8px",
                          },
                        }}
                      >
                        {prescriptionOptions.map((option: any) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            data-test-id={`prescription-select-${option.label}`}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </CompactSelect>
                    )}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {siteTemplate.site.name !== "" && (
                    <IconButton
                      id={`delete-${baseKey}`}
                      disabled={!isActive}
                      onClick={() => handleDelete(rowIndex, siteTemplate.id)}
                      size="large"
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  )}
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default TreatmentSitesTable;
